/* Companies — multi-tenant SaaS admin page.
   Super admin manages customer companies + their assigned plans.
   Plans are shown read-only here (full CRUD still at /plans). */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Plus, Trash2, Check, Edit3, Crown, Users as UsersIcon, Shield } from "lucide-react";
import { toast } from "sonner";

export default function Companies() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    const [c, p, u] = await Promise.all([
      api.get("/companies"),
      api.get("/plans"),
      isSuper ? api.get("/users").catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setCompanies(c.data || []);
    setPlans(p.data || []);
    setUsers(u.data || []);
  };
  useEffect(() => { refresh(); }, []);

  const del = async (cid) => {
    if (!window.confirm("Delete this company? Users will be detached.")) return;
    try { await api.delete(`/companies/${cid}`); toast.success("Company deleted"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const updatePlan = async (cid, planId) => {
    try { await api.put(`/companies/${cid}`, { plan_id: planId }); toast.success("Plan updated"); refresh(); }
    catch { toast.error("Failed"); }
  };

  const assignUser = async (uid, cid) => {
    try {
      if (cid === "__clear__") {
        const u = users.find((x) => x.id === uid);
        if (u?.company_id) await api.delete(`/companies/${u.company_id}/users/${uid}`);
      } else {
        await api.post(`/companies/${cid}/assign-user/${uid}`);
      }
      toast.success("User reassigned");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="companies-page">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex items-center gap-2"><Building2 className="size-3.5" /> Tenants</div>
          <h1 className="display text-3xl md:text-4xl font-black tracking-tighter mt-1">Companies & plans</h1>
          <p className="text-muted-foreground mt-1 text-sm">Each company is a SaaS tenant. The plan controls how many active candidates the WHOLE company can manage.</p>
        </div>
        {isSuper && <Button onClick={() => setCreating(true)} className="gap-2 lift" data-testid="company-new"><Plus className="size-4" /> New company</Button>}
      </div>

      {/* Companies grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="companies-grid">
        {companies.map((c) => {
          const plan = plans.find((p) => p.id === c.plan_id);
          const userCount = users.filter((u) => u.company_id === c.id).length;
          return (
            <Card key={c.id} className="border-border lift relative overflow-hidden" data-testid={`company-${c.id}`}>
              <CardContent className="p-5">
                {c.is_internal && (
                  <div className="absolute top-3 right-3 chip bg-emerald-500/15 text-emerald-700 text-[9px] uppercase tracking-wider font-bold flex items-center gap-1">
                    <Shield className="size-3" /> Internal
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg grid place-items-center text-white font-extrabold text-sm" style={{ background: plan?.color || "#94A3B8" }}>
                    {c.name?.[0] || "?"}
                  </div>
                  <div className="min-w-0">
                    <div className="display font-bold text-lg truncate">{c.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold mt-0.5 truncate">{c.slug || c.id.slice(0, 8)}</div>
                  </div>
                </div>
                {c.description && <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{c.description}</p>}
                <div className="mt-4 grid grid-cols-2 gap-2 text-center">
                  <div className="rounded-md bg-secondary p-2">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Users</div>
                    <div className="display font-extrabold text-lg flex items-center justify-center gap-1"><UsersIcon className="size-3.5" /> {userCount}</div>
                  </div>
                  <div className="rounded-md bg-secondary p-2">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground font-bold">Active limit</div>
                    <div className="display font-extrabold text-lg flex items-center justify-center gap-1" style={{ color: plan?.color }}><Crown className="size-3.5" /> {plan?.max_resumes === 0 ? "∞" : (plan?.max_resumes ?? "—")}</div>
                  </div>
                </div>
                {isSuper && !c.is_internal && (
                  <div className="mt-3 flex items-center gap-2">
                    <Select value={c.plan_id || ""} onValueChange={(v) => updatePlan(c.id, v)}>
                      <SelectTrigger className="h-8" data-testid={`company-plan-${c.id}`}><SelectValue placeholder="Plan" /></SelectTrigger>
                      <SelectContent>
                        {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.max_resumes === 0 ? "Unlimited" : `${p.max_resumes}`}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {isSuper && (
                  <div className="flex gap-1.5 mt-3">
                    <Button size="sm" variant="outline" onClick={() => setEditing(c)} className="gap-1" data-testid={`company-edit-${c.id}`}><Edit3 className="size-3.5" /> Edit</Button>
                    <Button size="sm" variant="ghost" className="text-destructive gap-1" disabled={c.is_internal} onClick={() => del(c.id)} data-testid={`company-del-${c.id}`}><Trash2 className="size-3.5" /></Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Users → Companies assignment */}
      {isSuper && (
        <Card className="border-border mb-8">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <UsersIcon className="size-4 text-primary" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">User assignments</div>
                <div className="display font-bold text-lg">Map users → companies</div>
              </div>
            </div>
            <div className="space-y-2" data-testid="company-user-list">
              {users.filter((u) => u.role !== "super_admin").map((u) => (
                <div key={u.id} className="flex items-center justify-between p-3 rounded-md border border-border">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{u.full_name}</div>
                    <div className="text-[11px] text-muted-foreground">{u.email} · {u.role.replace("_", " ")}</div>
                  </div>
                  <Select value={u.company_id || "__clear__"} onValueChange={(v) => assignUser(u.id, v)}>
                    <SelectTrigger className="w-[260px] h-9" data-testid={`company-user-select-${u.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__clear__">— Unassigned —</SelectItem>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.is_internal ? " · Internal" : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(editing || creating) && (
        <CompanyDialog company={editing} creating={creating} plans={plans} onClose={() => { setEditing(null); setCreating(false); refresh(); }} />
      )}
    </div>
  );
}

function CompanyDialog({ company, creating, plans, onClose }) {
  const [c, setC] = useState(company || { name: "", slug: "", description: "", contact_email: "", website: "", plan_id: plans.find((p) => p.is_default)?.id || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!c.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (creating) await api.post("/companies", c);
      else await api.put(`/companies/${company.id}`, c);
      toast.success(creating ? "Company created" : "Company updated");
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px]" data-testid="company-dialog">
        <DialogHeader><DialogTitle>{creating ? "New company" : "Edit company"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Company name</Label><Input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="company-input-name" /></div>
            <div><Label>Slug</Label><Input value={c.slug || ""} placeholder="acme-recruiters" onChange={(e) => setC({ ...c, slug: e.target.value })} /></div>
          </div>
          <div><Label>Description</Label><Textarea rows={2} value={c.description || ""} onChange={(e) => setC({ ...c, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact email</Label><Input type="email" value={c.contact_email || ""} onChange={(e) => setC({ ...c, contact_email: e.target.value })} /></div>
            <div><Label>Website</Label><Input value={c.website || ""} onChange={(e) => setC({ ...c, website: e.target.value })} /></div>
          </div>
          <div><Label>Plan</Label>
            <Select value={c.plan_id || ""} onValueChange={(v) => setC({ ...c, plan_id: v })}>
              <SelectTrigger data-testid="company-input-plan"><SelectValue /></SelectTrigger>
              <SelectContent>
                {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.max_resumes === 0 ? "Unlimited" : `${p.max_resumes} active candidates`}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="company-save"><Check className="size-4 mr-1" /> {saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
